import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Tag, Plus, Edit2, Trash2, Upload, Download, Phone, Mail, MessageCircle, MessageSquare, Send, Building2, Copy } from 'lucide-react';
import { contactsService, CHANNEL_FILTERS, UNFILTERABLE_CHANNELS } from 'api/services/contacts.service';
import { organizationsService } from 'api/services/organizations.service';
import { Contact, PaginatedResponse, CommunicationChannel } from 'types';
import api from 'api/axios.config';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';
import ContactEditModal from 'components/Contacts/ContactEditModal';
import { displayName } from 'utils/capturedFields';
import BulkImportModal from 'components/Contacts/BulkImportModal';
import { useAuth } from 'hooks/useAuth';
import OrganizationFilter from 'components/Filters/OrganizationFilter';

const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [data, setData] = useState<PaginatedResponse<Contact> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [channelFilter, setChannelFilter] = useState<CommunicationChannel | 'all'>('all');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [deleteConfirmContact, setDeleteConfirmContact] = useState<Contact | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Organization selector - default to current organization
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  // service_admin belongs here with the other two. getOrganizationId() already
  // treats all three the same — it returns undefined until an organisation is
  // picked — but only master and super admins were ever ASKED to pick one. A
  // service provider therefore got no prompt and no organisationId, and the
  // backend's no-organisation path reads the LOCAL call tables rather than
  // federating to the switches, which is where this estate's calls actually
  // live. The page came back empty and said nothing.
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
                            (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') &&
                            (!selectedOrgId || selectedOrgId === 'all');

  useEffect(() => {
    if (!needsOrgSelection) {
      fetchContacts();
    }
  }, [currentPage, searchQuery, channelFilter, selectedOrgId, needsOrgSelection]);

  const fetchContacts = async () => {
    if (needsOrgSelection) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const orgId = getOrganizationId();
    console.log('🔍 Contacts - organizationId:', orgId, 'selectedOrgId:', selectedOrgId, 'currentRole:', currentRole);
    const result = await contactsService.getContacts({
      page: currentPage,
      // `size` is the name the endpoint binds. It was `perPage`, which Spring
      // dropped, so every page came back at the backend default of 20 while the
      // pager below was told 10 — 20 rows on screen, page counts and "showing
      // X to Y" computed from a page size that was never used.
      size: ITEMS_PER_PAGE,
      search: searchQuery,
      channel: channelFilter === 'all' ? undefined : channelFilter,
      organizationId: orgId,
    });

    console.log('📦 Contacts API Response:', result);

    if (result.success && result.data) {
      setData(result.data);
    } else {
      setError(result.error || 'Failed to fetch contacts');
    }

    setLoading(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleChannelFilter = (channel: CommunicationChannel | 'all') => {
    setChannelFilter(channel);
    setCurrentPage(1);
  };

  const handleAddContact = () => {
    setSelectedContact(null);
    setIsContactModalOpen(true);
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setIsContactModalOpen(true);
  };

  const handleContactSuccess = () => {
    fetchContacts();
    setIsContactModalOpen(false);
  };

  const handleBulkImportSuccess = () => {
    fetchContacts();
    setIsBulkImportModalOpen(false);
  };

  const handleDeleteClick = (contact: Contact) => {
    setDeleteConfirmContact(contact);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmContact) return;

    setDeleting(true);
    const result = await contactsService.deleteContact(deleteConfirmContact._uid);

    if (result.success) {
      fetchContacts();
      setDeleteConfirmContact(null);
    } else {
      setError(result.error || 'Failed to delete contact');
    }

    setDeleting(false);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmContact(null);
  };

  const handleExport = async () => {
    try {
      const orgId = getOrganizationId();

      // Use axios for proper authentication and base URL handling
      const response = await api.get('/v1/contacts/export', {
        params: orgId ? { organizationId: orgId } : {},
        responseType: 'blob', // Important: handle binary data
      });

      // Create blob from response
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'contacts_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting contacts:', error);
      setError('Failed to export contacts. Please try again.');
    }
  };

  const getChannelIcon = (channel: CommunicationChannel) => {
    switch (channel) {
      case 'telephony':
        return <Phone className="w-4 h-4" />;
      case 'whatsapp':
        return <MessageCircle className="w-4 h-4" />;
      case 'sms':
        return <MessageSquare className="w-4 h-4" />;
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'rcs':
        return <Send className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getPrimaryPhone = (contact: Contact) => {
    if (!contact.phone_numbers || contact.phone_numbers.length === 0) return null;
    return contact.phone_numbers.find(p => p.is_primary)?.number || contact.phone_numbers[0]?.number;
  };

  const getPrimaryEmail = (contact: Contact) => {
    if (!contact.email_addresses || contact.email_addresses.length === 0) return null;
    return contact.email_addresses.find(e => e.is_primary)?.email || contact.email_addresses[0]?.email;
  };

  if (loading && !data) return <LoadingSpinner />;
  if (error && !data) return <ErrorMessage message={error} onRetry={fetchContacts} />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Users className="w-7 h-7" />
            Contacts
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Total: {data.total} contacts across all channels
          </p>
        </div>
        <div className="flex gap-2">
          {/* The way in to the duplicates review. Without a link the page exists and
              nobody finds it — which is exactly what happened to two pages this week. */}
          <button
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
            onClick={() => navigate('/user-directory/contacts/duplicates')}
            title="Contacts that share a number or an address"
          >
            <Copy className="w-5 h-5" />
            Duplicates
          </button>
          <button
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            onClick={handleExport}
            disabled={needsOrgSelection || !data || data.total === 0}
            title="Export all contacts to CSV"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
          <button
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            onClick={() => setIsBulkImportModalOpen(true)}
          >
            <Upload className="w-5 h-5" />
            Bulk Import
          </button>
          <button
            className="tl-btn tl-btn-primary"
            onClick={handleAddContact}
          >
            <Plus className="w-5 h-5" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId);
              } else {
                setSelectedOrgId(value || 'all');
              }
              setCurrentPage(1);
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show friendly message when organization selection is required */}
      {needsOrgSelection && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300">Select an Organization</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                Please select an organization from the dropdown above to view contacts.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {!needsOrgSelection && <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search contacts by name, company, phone, or email..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Channel Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by Channel:</span>
          <button
            onClick={() => handleChannelFilter('all')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
              channelFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            All Channels
          </button>
          {/* Only the channels the server can answer for. Membership is not stored;
              it is derived from contact_phones / contact_emails, and CHANNEL_FILTERS
              is the single list both this row and the API agree on. */}
          {CHANNEL_FILTERS.map(({ channel, label }) => (
            <button
              key={channel}
              onClick={() => handleChannelFilter(channel)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition flex items-center gap-1 ${
                channelFilter === channel
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {getChannelIcon(channel)}
              {label}
            </button>
          ))}
          {/* Shown, disabled, and explained. Clicking this used to return all 5,822
              contacts and present them as the SMS subset. */}
          {UNFILTERABLE_CHANNELS.map(({ channel, label, reason }) => (
            <button
              key={channel}
              type="button"
              disabled
              title={reason}
              className="px-3 py-1 rounded-lg text-sm font-medium flex items-center gap-1 bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
            >
              {getChannelIcon(channel)}
              {label}
            </button>
          ))}
        </div>
      </div>}

      {/* Contacts List */}
      {!needsOrgSelection && <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Phone / Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Channels
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Labels
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Contact
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.data && data.data.length > 0 && data.data.map((contact) => (
                <tr key={contact._uid} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {displayName(contact)[0]?.toUpperCase()}
                      </div>
                      <div>
                        {/* displayName, not contact.name: contacts created while
                            cpass read only `name` — and the agent's tool sent
                            first_name/last_name — are stored called
                            "+919024685946". The backend composes the name for
                            new leads, but nothing rewrites rows already saved. */}
                        {/* The name is the way in to the profile. A page nothing
                            links to is a page nobody finds. */}
                        <button
                          type="button"
                          onClick={() => navigate(`/user-directory/contacts/${contact._uid}`)}
                          className="font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-left"
                        >
                          {displayName(contact)}
                        </button>
                        {contact.company && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{contact.company}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm space-y-1">
                      {getPrimaryPhone(contact) && (
                        <div className="flex items-center gap-1 text-gray-900 dark:text-white">
                          <Phone className="w-3 h-3 text-gray-400" />
                          {getPrimaryPhone(contact)}
                        </div>
                      )}
                      {getPrimaryEmail(contact) && (
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                          <Mail className="w-3 h-3 text-gray-400" />
                          {getPrimaryEmail(contact)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {contact.channels && contact.channels.length > 0 && contact.channels.map((ch, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs flex items-center gap-1"
                          title={ch.channel}
                        >
                          {getChannelIcon(ch.channel)}
                          {ch.unread_count > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white rounded-full text-[10px]">
                              {ch.unread_count}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {contact.labels && contact.labels.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {contact.labels.slice(0, 3).map((label, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400 rounded text-xs flex items-center gap-1"
                          >
                            <Tag className="w-3 h-3" />
                            {label}
                          </span>
                        ))}
                        {contact.labels.length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs">
                            +{contact.labels.length - 3}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">No labels</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {contact.last_contact_at ? (
                      <div className="text-sm">
                        <p className="text-gray-900 dark:text-white">
                          {new Date(contact.last_contact_at).toLocaleDateString()}
                        </p>
                        {contact.last_contact_channel && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            {getChannelIcon(contact.last_contact_channel)}
                            {contact.last_contact_channel}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Never</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleEditContact(contact)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(contact)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.data.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <Pagination
              currentPage={currentPage}
              totalPages={data.totalPages}
              onPageChange={setCurrentPage}
              totalItems={data.total}
              // The size the server paged by, not the one we asked for. totalPages
              // already comes from the server's arithmetic; taking the row count
              // from anywhere else is what let the two disagree.
              itemsPerPage={data.perPage || ITEMS_PER_PAGE}
            />
          </div>
        )}

        {data.data.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No contacts found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchQuery || channelFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Start by adding contacts or importing them'}
            </p>
          </div>
        )}
      </div>}

      {/* Modals */}
      <ContactEditModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSuccess={handleContactSuccess}
        editContact={selectedContact}
      />
      <BulkImportModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onSuccess={handleBulkImportSuccess}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Contact</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>{deleteConfirmContact.name}</strong>?
              This will remove the contact from all channels. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                className="px-6 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
